/**
 * Enterprise API Error Handler
 *
 * Provides unified error handling and user-friendly messaging:
 * - Error classification and normalization
 * - User-friendly error messages
 * - Visual notification system
 * - Automatic error recovery suggestions
 * - Error logging and tracking
 *
 * Part of Zanova Enterprise Architecture - Skill #2
 *
 * REPLACES: Inconsistent error handling across all files
 */

class EnterpriseAPIErrorHandler {
  constructor(config = {}) {
    this.config = {
      showNotifications: config.showNotifications !== false, // true by default
      notificationDuration: config.notificationDuration || 3000, // 3 seconds
      logErrors: config.logErrors !== false, // true by default
      enableRetryPrompts: config.enableRetryPrompts !== false, // true by default
      // Enable debug logging only in development
      enableDebugLogging: config.enableDebugLogging !== undefined ? config.enableDebugLogging : (
        window.location.hostname === 'localhost' ||
        window.location.hostname.includes('127.0.0.1') ||
        window.location.hostname.includes('.myshopify.com') ||
        window.location.search.includes('debug=true')
      ),
      ...config
    };

    // Active notifications tracking
    this.activeNotifications = new Map();

    // Error statistics
    this.stats = {
      totalErrors: 0,
      errorsByType: {},
      lastError: null
    };

    this.log('EnterpriseAPIErrorHandler initialized', this.config);
  }

  /**
   * Predefined error messages for consistent UX
   */
  static ERRORS = {
    // Cart errors
    OUT_OF_STOCK: {
      message: 'Sorry, this item is currently out of stock.',
      suggestion: 'Try checking back later or choose a different variant.',
      type: 'warning'
    },
    MAX_QUANTITY: {
      message: 'Maximum available quantity reached.',
      suggestion: 'The cart has been updated with the maximum available quantity.',
      type: 'warning'
    },
    CART_ADD_FAILED: {
      message: 'Unable to add item to bag.',
      suggestion: 'Please try again in a moment.',
      type: 'error'
    },
    CART_UPDATE_FAILED: {
      message: 'Unable to update cart.',
      suggestion: 'Please refresh the page and try again.',
      type: 'error'
    },

    // Network errors
    NETWORK_ERROR: {
      message: 'Connection issue detected.',
      suggestion: 'Please check your internet connection and try again.',
      type: 'error'
    },
    TIMEOUT: {
      message: 'Request timed out.',
      suggestion: 'The server is taking too long to respond. Please try again.',
      type: 'warning'
    },

    // Product errors
    PRODUCT_NOT_FOUND: {
      message: 'Product not found.',
      suggestion: 'This product may no longer be available.',
      type: 'error'
    },
    VARIANT_NOT_FOUND: {
      message: 'Selected variant not available.',
      suggestion: 'Please choose a different option.',
      type: 'warning'
    },

    // Generic errors
    UNKNOWN_ERROR: {
      message: 'Something went wrong.',
      suggestion: 'Please try again or contact support if the issue persists.',
      type: 'error'
    },
    ABORTED: {
      message: 'Request cancelled.',
      suggestion: null,
      type: 'info'
    }
  };

  /**
   * Handle error with classification and user notification
   *
   * @param {Error} error - Error object
   * @param {object} context - Additional context
   * @returns {object} - Normalized error with user message
   */
  handleError(error, context = {}) {
    this.stats.totalErrors++;
    this.stats.lastError = {
      error,
      context,
      timestamp: new Date().toISOString()
    };

    // Classify error
    const errorInfo = this.classifyError(error, context);

    // Track error type
    const errorType = errorInfo.code || 'UNKNOWN';
    this.stats.errorsByType[errorType] = (this.stats.errorsByType[errorType] || 0) + 1;

    // Log error if enabled
    if (this.config.logErrors) {
      this.logError(errorInfo, context);
    }

    // Show notification if enabled
    if (this.config.showNotifications && errorInfo.message) {
      this.showNotification(
        errorInfo.message,
        errorInfo.type,
        this.config.notificationDuration,
        errorInfo.suggestion
      );
    }

    return errorInfo;
  }

  /**
   * Classify error and determine appropriate message
   */
  classifyError(error, context = {}) {
    // Check for pre-defined error codes
    if (error.code && EnterpriseAPIErrorHandler.ERRORS[error.code]) {
      return {
        ...EnterpriseAPIErrorHandler.ERRORS[error.code],
        code: error.code,
        originalError: error
      };
    }

    // HTTP status-based classification
    if (error.status) {
      switch (error.status) {
        case 404:
          return {
            ...EnterpriseAPIErrorHandler.ERRORS.PRODUCT_NOT_FOUND,
            code: 'NOT_FOUND',
            originalError: error
          };

        case 422:
          // Unprocessable entity - usually out of stock
          return {
            ...EnterpriseAPIErrorHandler.ERRORS.OUT_OF_STOCK,
            code: 'OUT_OF_STOCK',
            originalError: error
          };

        case 429:
          return {
            message: 'Too many requests.',
            suggestion: 'Please wait a moment before trying again.',
            type: 'warning',
            code: 'RATE_LIMITED',
            originalError: error
          };

        case 500:
        case 502:
        case 503:
          return {
            message: 'Server error occurred.',
            suggestion: 'Our team has been notified. Please try again later.',
            type: 'error',
            code: 'SERVER_ERROR',
            originalError: error
          };

        default:
          return {
            ...EnterpriseAPIErrorHandler.ERRORS.UNKNOWN_ERROR,
            code: `HTTP_${error.status}`,
            originalError: error
          };
      }
    }

    // Network error detection
    if (!navigator.onLine || error.message?.includes('network') || error.message?.includes('fetch')) {
      return {
        ...EnterpriseAPIErrorHandler.ERRORS.NETWORK_ERROR,
        code: 'NETWORK_ERROR',
        originalError: error
      };
    }

    // Abort error
    if (error.name === 'AbortError') {
      return {
        ...EnterpriseAPIErrorHandler.ERRORS.ABORTED,
        code: 'ABORTED',
        originalError: error
      };
    }

    // Timeout error
    if (error.name === 'TimeoutError' || context.timeout) {
      return {
        ...EnterpriseAPIErrorHandler.ERRORS.TIMEOUT,
        code: 'TIMEOUT',
        originalError: error
      };
    }

    // Default fallback
    return {
      ...EnterpriseAPIErrorHandler.ERRORS.UNKNOWN_ERROR,
      code: 'UNKNOWN',
      originalError: error
    };
  }

  /**
   * Show visual notification to user
   *
   * @param {string} message - Main message
   * @param {string} type - Notification type (error/warning/info/success)
   * @param {number} duration - Duration in ms (0 = permanent)
   * @param {string} suggestion - Optional suggestion text
   * @returns {string} - Notification ID
   */
  showNotification(message, type = 'error', duration = 3000, suggestion = null) {
    const notificationId = `notification-${Date.now()}`;

    // Create notification element
    const notification = document.createElement('div');
    notification.id = notificationId;
    notification.className = `enterprise-notification enterprise-notification--${type}`;
    notification.setAttribute('role', 'alert');
    notification.setAttribute('aria-live', 'polite');

    // Build notification content
    let html = `
      <div class="enterprise-notification__content">
        <div class="enterprise-notification__icon">
          ${this.getIconForType(type)}
        </div>
        <div class="enterprise-notification__text">
          <div class="enterprise-notification__message">${message}</div>
          ${suggestion ? `<div class="enterprise-notification__suggestion">${suggestion}</div>` : ''}
        </div>
        <button class="enterprise-notification__close" aria-label="Close notification">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M1 1L13 13M1 13L13 1" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
          </svg>
        </button>
      </div>
    `;

    notification.innerHTML = html;

    // Add styles if not already present
    this.injectStyles();

    // Add to DOM
    this.getNotificationContainer().appendChild(notification);

    // Add close handler
    const closeButton = notification.querySelector('.enterprise-notification__close');
    closeButton.addEventListener('click', () => {
      this.closeNotification(notificationId);
    });

    // Track active notification
    const timeoutId = duration > 0
      ? setTimeout(() => this.closeNotification(notificationId), duration)
      : null;

    this.activeNotifications.set(notificationId, {
      element: notification,
      timeoutId,
      type
    });

    // Animate in
    requestAnimationFrame(() => {
      notification.classList.add('enterprise-notification--visible');
    });

    this.log(`Showed notification: ${type}`, { message, suggestion });
    return notificationId;
  }

  /**
   * Close notification
   */
  closeNotification(notificationId) {
    const notification = this.activeNotifications.get(notificationId);
    if (!notification) return;

    // Clear timeout if exists
    if (notification.timeoutId) {
      clearTimeout(notification.timeoutId);
    }

    // Animate out
    notification.element.classList.remove('enterprise-notification--visible');

    // Remove from DOM after animation
    setTimeout(() => {
      notification.element.remove();
      this.activeNotifications.delete(notificationId);
    }, 300);
  }

  /**
   * Close all notifications
   */
  clearNotifications() {
    this.activeNotifications.forEach((_, notificationId) => {
      this.closeNotification(notificationId);
    });
  }

  /**
   * Get notification container (create if doesn't exist)
   */
  getNotificationContainer() {
    let container = document.getElementById('enterprise-notifications');

    if (!container) {
      container = document.createElement('div');
      container.id = 'enterprise-notifications';
      container.className = 'enterprise-notifications';
      document.body.appendChild(container);
    }

    return container;
  }

  /**
   * Get icon SVG for notification type
   */
  getIconForType(type) {
    const icons = {
      error: '<svg width="20" height="20" viewBox="0 0 20 20" fill="none"><circle cx="10" cy="10" r="9" stroke="currentColor" stroke-width="2"/><path d="M10 6V11" stroke="currentColor" stroke-width="2" stroke-linecap="round"/><circle cx="10" cy="14" r="1" fill="currentColor"/></svg>',
      warning: '<svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M10 2L18 17H2L10 2Z" stroke="currentColor" stroke-width="2" stroke-linejoin="round"/><path d="M10 8V12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/><circle cx="10" cy="15" r="1" fill="currentColor"/></svg>',
      info: '<svg width="20" height="20" viewBox="0 0 20 20" fill="none"><circle cx="10" cy="10" r="9" stroke="currentColor" stroke-width="2"/><path d="M10 10V14" stroke="currentColor" stroke-width="2" stroke-linecap="round"/><circle cx="10" cy="7" r="1" fill="currentColor"/></svg>',
      success: '<svg width="20" height="20" viewBox="0 0 20 20" fill="none"><circle cx="10" cy="10" r="9" stroke="currentColor" stroke-width="2"/><path d="M6 10L9 13L14 7" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>'
    };

    return icons[type] || icons.info;
  }

  /**
   * Inject notification styles
   */
  injectStyles() {
    if (document.getElementById('enterprise-notification-styles')) return;

    const style = document.createElement('style');
    style.id = 'enterprise-notification-styles';
    style.textContent = `
      .enterprise-notifications {
        position: fixed;
        top: 20px;
        right: 20px;
        z-index: 100000;
        display: flex;
        flex-direction: column;
        gap: 12px;
        max-width: 400px;
        pointer-events: none;
      }

      .enterprise-notification {
        background: white;
        border-radius: 8px;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        padding: 16px;
        opacity: 0;
        transform: translateX(100%);
        transition: all 0.3s ease;
        pointer-events: auto;
      }

      .enterprise-notification--visible {
        opacity: 1;
        transform: translateX(0);
      }

      .enterprise-notification__content {
        display: flex;
        gap: 12px;
        align-items: flex-start;
      }

      .enterprise-notification__icon {
        flex-shrink: 0;
      }

      .enterprise-notification--error .enterprise-notification__icon { color: #dc2626; }
      .enterprise-notification--warning .enterprise-notification__icon { color: #f59e0b; }
      .enterprise-notification--info .enterprise-notification__icon { color: #3b82f6; }
      .enterprise-notification--success .enterprise-notification__icon { color: #10b981; }

      .enterprise-notification__text {
        flex: 1;
        min-width: 0;
      }

      .enterprise-notification__message {
        font-size: 14px;
        font-weight: 500;
        color: #111;
        margin-bottom: 4px;
      }

      .enterprise-notification__suggestion {
        font-size: 12px;
        color: #666;
      }

      .enterprise-notification__close {
        flex-shrink: 0;
        background: none;
        border: none;
        padding: 0;
        cursor: pointer;
        color: #999;
        transition: color 0.2s;
      }

      .enterprise-notification__close:hover {
        color: #333;
      }

      @media (max-width: 768px) {
        .enterprise-notifications {
          left: 12px;
          right: 12px;
          top: 12px;
          max-width: none;
        }
      }
    `;

    document.head.appendChild(style);
  }

  /**
   * Log error to console
   */
  logError(errorInfo, context) {
    window.debugLogger.error('[EnterpriseAPI Error]', {
      code: errorInfo.code,
      message: errorInfo.message,
      suggestion: errorInfo.suggestion,
      context,
      originalError: errorInfo.originalError,
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Utility logging (only in development)
   */
  log(message, data = null) {
    if (!this.config.enableDebugLogging) return;

    if (data) {
      window.debugLogger.log(`[EnterpriseErrorHandler] ${message}`, data);
    } else {
      window.debugLogger.log(`[EnterpriseErrorHandler] ${message}`);
    }
  }

  /**
   * Get error statistics
   */
  getStats() {
    return { ...this.stats };
  }

  /**
   * Reset statistics
   */
  resetStats() {
    this.stats = {
      totalErrors: 0,
      errorsByType: {},
      lastError: null
    };
  }
}

// Export for use in theme
if (typeof window !== 'undefined') {
  window.EnterpriseAPIErrorHandler = EnterpriseAPIErrorHandler;

  // Create global error handler instance
  window.errorHandler = new EnterpriseAPIErrorHandler({
    showNotifications: true,
    notificationDuration: 3000,
    logErrors: true
  });

  window.debugLogger?.log('✅ EnterpriseAPIErrorHandler loaded');
  window.debugLogger?.log('Global instance available at: window.errorHandler');
}
